import React from 'react';
import PropTypes from 'prop-types';
import { Button as MuiButton } from '@mui/material';
import { Icon } from './Icon';
import './Button.css';

/**
 * Unified Onyx Button — Figma Primary/Secondary + product density.
 * variant: primary | secondary | ghost | danger
 * size: compact (table/row actions) | dense (product default) | large (Figma marketing)
 */
export function Button({
  children,
  variant = 'primary',
  size = 'dense',
  chevron = false,
  iconOnly = false,
  className = '',
  ...props
}) {
  const muiVariant =
    variant === 'secondary' || variant === 'ghost' ? 'outlined' : 'contained';
  const color = variant === 'danger' ? 'error' : 'primary';
  const muiSize = size === 'large' ? 'large' : size === 'compact' ? 'small' : 'medium';

  return (
    <MuiButton
      variant={muiVariant}
      color={color}
      size={muiSize}
      className={[
        'onyx-button',
        `onyx-button--${variant}`,
        `onyx-button--${size}`,
        iconOnly ? 'onyx-button--icon' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      data-testid="onyx-button"
      data-variant={variant}
      data-size={size}
      data-icon-only={iconOnly ? 'true' : undefined}
      endIcon={chevron ? <Icon name="chevron" size="sm" /> : undefined}
      {...props}
    >
      {children}
    </MuiButton>
  );
}

Button.propTypes = {
  children: PropTypes.node,
  variant: PropTypes.oneOf(['primary', 'secondary', 'ghost', 'danger']),
  size: PropTypes.oneOf(['compact', 'dense', 'large']),
  chevron: PropTypes.bool,
  iconOnly: PropTypes.bool,
  className: PropTypes.string,
};

/** @deprecated Use Button variant="primary" */
export function OnyxPrimaryButton({
  children,
  chevron = true,
  className = '',
  size = 'dense',
  ...props
}) {
  const buttonSize = size === 'large' ? 'large' : 'dense';
  return (
    <Button
      variant="primary"
      size={buttonSize}
      chevron={chevron}
      className={className}
      {...props}
      data-testid="onyx-primary-button"
    >
      {children}
    </Button>
  );
}

OnyxPrimaryButton.propTypes = {
  ...Button.propTypes,
  size: PropTypes.oneOf(['dense', 'large', 'medium', 'small']),
};

/** @deprecated Use Button variant="secondary" */
export function OnyxSecondaryButton({
  children,
  className = '',
  size = 'dense',
  ...props
}) {
  const buttonSize = size === 'large' ? 'large' : 'dense';
  return (
    <Button
      variant="secondary"
      size={buttonSize}
      className={className}
      {...props}
      data-testid="onyx-secondary-button"
    >
      {children}
    </Button>
  );
}

OnyxSecondaryButton.propTypes = Button.propTypes;

export default Button;
